"use client";

import {
  OutcomeToolbarFilterSelect,
  useOutcomeToolbarPills,
  type OutcomeToolbarFilterAction,
} from "@/components/ui/primitives/outcome-toolbar-filter-select";
import {
  applySpecificationFilterValues,
  isSpecificationUnassignedMode,
  specificationFilterTriggerPills,
  splitSpecificationFilterValues,
  type SpecificationFilterAction,
} from "@/lib/constants/analysis-list-filters";
import type { SearchableSelectOption } from "@/components/ui/primitives/searchable-select";
import type { ReactNode } from "react";

const UNASSIGNED_NOTICE =
  "Specification names were cleared. List shows analyses with no spec. Pick a name or Select all to leave this mode.";

export interface SpecificationFilterSelectProps {
  readonly value: string[];
  readonly onChange: (value: string[]) => void;
  readonly options: SearchableSelectOption[];
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly disabled?: boolean;
  readonly dropdownHeader?: ReactNode;
  readonly "aria-label"?: string;
}

function applySpecificationToolbarAction(
  current: readonly string[],
  action: OutcomeToolbarFilterAction,
): string[] {
  const specAction: SpecificationFilterAction =
    action.type === "set-ids"
      ? { type: "set-spec-ids", specIds: action.ids }
      : action;
  return applySpecificationFilterValues(current, specAction);
}

export function SpecificationFilterSelect({
  value,
  onChange,
  options,
  placeholder = "Specification",
  searchPlaceholder = "Search specification…",
  disabled = false,
  dropdownHeader,
  "aria-label": ariaLabel,
}: SpecificationFilterSelectProps) {
  const split = splitSpecificationFilterValues(value);
  const pills = useOutcomeToolbarPills(
    value,
    options,
    specificationFilterTriggerPills,
  );

  return (
    <OutcomeToolbarFilterSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      disabled={disabled}
      dropdownHeader={dropdownHeader}
      aria-label={ariaLabel}
      selectedIds={split.specificationIds}
      unassignedMode={isSpecificationUnassignedMode(value)}
      pass={split.specPassed.includes("true")}
      fail={split.specPassed.includes("false")}
      unassignedNotice={UNASSIGNED_NOTICE}
      pills={pills}
      applyAction={(action) => applySpecificationToolbarAction(value, action)}
    />
  );
}
